/**
 * HelpModal — static FAQ + contact-support CTA.
 *
 * Content lives in i18n.jsx under help.* keys. We pull the categories
 * and Q&A pairs out into a hand-curated structure here so the
 * rendering layer can stay generic — adding a new question is just
 * an i18n key change and one entry in the FAQ array below.
 *
 * Search filters on the visible (translated) title and body. We keep
 * the filter cheap (substring match on the lowercase strings) — there
 * are ~25 items, no need for fuzzy matching.
 *
 * "Contact support" path:
 *   1. GET /api/managers (returns staff a customer can DM — already
 *      role-filtered server-side).
 *   2. POST /api/dms with the first manager/admin to get-or-create the
 *      thread (the endpoint is idempotent — a customer↔staff DM is
 *      unique on the user pair).
 *   3. Close the modal and switch the parent app to that DM by
 *      calling `onOpenDm(dmId)`.
 *
 * No new schema. Reuses the existing DM substrate. If we ever want a
 * "support" persona that's not a single staff user, we add a
 * `support_recipient_user_id` setting in admin-settings and pick that
 * here instead of `getManagers()[0]`.
 */
import { useState, useMemo } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Modal, Inp, Btn, Mono, Serif } from "./ui.jsx";
import { useT } from "../i18n.jsx";
import { useToast } from "./toast.jsx";

// FAQ structure. Each section has a category label key and a list of
// Q&A pairs (keys into the i18n flat map). Order matters — this is
// the order users see them in.
const FAQ = [
  { cat: "help.cat.start", items: [
    ["help.q.what",        "help.a.what"],
    ["help.q.start",       "help.a.start"],
    ["help.q.commission",  "help.a.commission"],
  ]},
  { cat: "help.cat.catalog", items: [
    ["help.q.forward",     "help.a.forward"],
    ["help.q.markup",      "help.a.markup"],
    ["help.q.bgcode",      "help.a.bgcode"],
    ["help.q.search",      "help.a.search"],
  ]},
  { cat: "help.cat.orders", items: [
    ["help.q.order",       "help.a.order"],
    ["help.q.cashback",    "help.a.cashback"],
    ["help.q.cashbackUse", "help.a.cashbackUse"],
    ["help.q.referral",    "help.a.referral"],
  ]},
  { cat: "help.cat.curated", items: [
    ["help.q.curated",        "help.a.curated"],
    ["help.q.curated.start",  "help.a.curated.start"],
    ["help.q.curated.status", "help.a.curated.status"],
  ]},
  { cat: "help.cat.account", items: [
    ["help.q.application", "help.a.application"],
    ["help.q.approve",     "help.a.approve"],
    ["help.q.lang",        "help.a.lang"],
    ["help.q.nick",        "help.a.nick"],
    ["help.q.logout",      "help.a.logout"],
  ]},
  { cat: "help.cat.trouble", items: [
    ["help.q.trouble.notif",   "help.a.trouble.notif"],
    ["help.q.trouble.forward", "help.a.trouble.forward"],
    ["help.q.trouble.pwa",     "help.a.trouble.pwa"],
    ["help.q.trouble.ru",      "help.a.trouble.ru"],
  ]},
];

export function HelpModal({ user, onClose, onOpenDm }) {
  const t = useT();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [contacting, setContacting] = useState(false);

  // Filter on translated strings. Cheap substring match — there are
  // only ~25 items so we re-run on every keystroke without memoizing
  // hard. useMemo here is mostly for clarity.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return FAQ;
    return FAQ
      .map(({ cat, items }) => ({
        cat,
        items: items.filter(([qk, ak]) =>
          (t(qk) + " " + t(ak)).toLowerCase().includes(q),
        ),
      }))
      .filter(({ items }) => items.length > 0);
  }, [search, t]);

  const totalShown = filtered.reduce((n, s) => n + s.items.length, 0);

  const openSupportDm = async () => {
    setContacting(true);
    try {
      // /api/managers returns staff the current user can DM. For a
      // customer, that's everyone with role manager/admin. Pick the
      // first one — designating a "support persona" is a future
      // refinement (see file header comment).
      const managers = await api.getManagers();
      if (!managers || managers.length === 0) {
        toast.error(t("help.contact.fail"));
        return;
      }
      // /api/managers returns { uid, email, nickname, ... } — note the
      // field name is `uid`, not `id`. Earlier version of this handler
      // tried `target.id` (undefined) and the server rejected with
      // "Не указан получатель".
      const target = managers[0];
      const dm = await api.createDm(target.uid);
      onClose();
      // Switch to the DM tab and open this thread. Parent owns nav.
      if (onOpenDm) onOpenDm(dm.id);
    } catch (e) {
      toast.error(e.message || t("help.contact.fail"));
    } finally {
      setContacting(false);
    }
  };

  return (
    <Modal title={t("help.title")} onClose={onClose} width={640}>
      <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                    fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                    marginBottom: 16, lineHeight: 1.5 }}>
        {t("help.subtitle")}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Inp value={search} onChange={setSearch}
             placeholder={t("help.search")}
             autoComplete="off" />
      </div>

      {totalShown === 0 ? (
        <div style={{ padding: 30, textAlign: "center",
                      background: T_NEW.paperSoft,
                      border: `1px dashed ${T_NEW.ink12}`,
                      borderRadius: T_NEW.r2,
                      color: T_NEW.ink56,
                      fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
          {t("help.empty")}
        </div>
      ) : (
        filtered.map(({ cat, items }) => (
          <div key={cat} style={{ marginBottom: 22 }}>
            <Mono size={T_NEW.micro} color={T_NEW.ink56}
                  style={{ display: "block", marginBottom: 10,
                           fontWeight: 700, letterSpacing: 0.5,
                           textTransform: "uppercase" }}>
              {t(cat)}
            </Mono>
            {items.map(([qk, ak]) => {
              const isOpen = openId === qk;
              return (
                <div key={qk}
                     style={{ border: `1px solid ${T_NEW.ink12}`,
                              borderRadius: T_NEW.r2,
                              marginBottom: 8,
                              background: T_NEW.paperRaised,
                              overflow: "hidden" }}>
                  <button type="button"
                          onClick={() => setOpenId(isOpen ? null : qk)}
                          style={{ display: "flex", width: "100%",
                                   textAlign: "left",
                                   alignItems: "center",
                                   justifyContent: "space-between",
                                   gap: 12, padding: "12px 14px",
                                   background: "transparent",
                                   border: "none", cursor: "pointer",
                                   fontFamily: T_NEW.fontSans,
                                   fontSize: 14, color: T_NEW.ink,
                                   fontWeight: 500 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>{t(qk)}</span>
                    <span style={{ flexShrink: 0, color: T_NEW.ink56,
                                   fontSize: 18, lineHeight: 1,
                                   transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                                   transition: "transform 160ms" }}>
                      ⌄
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: "0 14px 14px",
                                  fontSize: 13.5, color: T_NEW.ink,
                                  lineHeight: 1.6,
                                  fontFamily: T_NEW.fontSans,
                                  whiteSpace: "pre-wrap" }}>
                      {t(ak)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}

      {/* Contact-support card. Sticks at the bottom of the modal as
          the escalation path. Hidden for staff — they ARE the support
          team. */}
      {user?.role === "customer" && (
        <div style={{ marginTop: 22, padding: "16px 16px",
                      background: T_NEW.paperSoft,
                      border: `1px solid ${T_NEW.ink12}`,
                      borderRadius: T_NEW.r2 }}>
          <Serif size={15} color={T_NEW.ink}
                 style={{ fontWeight: 600, marginBottom: 6 }}>
            {t("help.contact.title")}
          </Serif>
          <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                        fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                        lineHeight: 1.5, marginBottom: 12 }}>
            {t("help.contact.body")}
          </div>
          <Btn variant="primary" onClick={openSupportDm} disabled={contacting}>
            {contacting ? t("help.contact.opening") : t("help.contact.cta")}
          </Btn>
        </div>
      )}
    </Modal>
  );
}
